/**
 * ProtectedRoute component - Wraps routes that require authentication
 * UI gating uses portal auth state (authState); redirects use AuthRedirectGuard from AuthContext.
 */
import React, { useRef, useState, useEffect, useSyncExternalStore, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY,
  hasPriorPortalAuthentication,
} from '@hsus-dev/session-policy';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useAuth, AuthRedirectGuard } from '../../context/AuthContext';
import { useActiveRole } from '../../context/ActiveRoleContext';
import { LoadingSpinner } from '../Loading/LoadingSpinner';
import { UserProfileApi } from '../../api/userProfile';
import { getCachedOrFetch, isEntraObjectId, ProfileFetchSuppressedError } from '../../utils/userProfileCache.utils';
import { AUTH_API_CONFIG } from '../../config/api.config';
import { getAuthProvider } from '../../services/authApi.service';
import { MsalAuthProvider } from '../../services/msalAuthProvider.service';
import { getMsalInstance } from '../../services/msalBootstrap.service';
import { PROTECTED_ROUTE_LOADING_COPY } from './protectedRouteLoadingCopy';
import { isBrowserMsalOAuthRedirect } from '../../utils/msalOAuthRedirect.utils';
import { hasEmptyMsalAccountCache } from '../../utils/msalAccountPresence.utils';
import {
  getLicenseGate,
  setLicenseGate,
  subscribeLicenseGate,
  getLicenseGateSnapshot,
} from '../../utils/licenseGate';

/** Route-area spinner with visible progress text (aria matches for screen readers). */
const routeProgressSpinner = (message: string) => (
  <LoadingSpinner message={message} ariaLabel={message} />
);

const SUSPENSE_PAGE_FALLBACK = routeProgressSpinner(
  PROTECTED_ROUTE_LOADING_COPY.loadingPage,
);

/** Fills main column height so page spinners center below the nav, not at the top. */
const withRouteShell = (inner: React.ReactNode) => (
  <div className="flex min-h-0 w-full flex-1 flex-col">{inner}</div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requiredRoles?: string[];
}

const ProtectedRouteComponent: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requiredRoles,
}) => {
  // Render counter for debugging - tracks if component is re-rendering unnecessarily
  const renderCount = useRef(0);
  const lastRenderTime = useRef<number>(Date.now());
  renderCount.current += 1;
  
  const { authState, isLoading, isLoggingOut, isLoggingIn, user, refreshAuthState } = useAuth();
  const { activeRole, availableRoles } = useActiveRole();
  const location = useLocation();
  const hasRedirectedRef = useRef<string | null>(null);
  // Tearing-safe read of the external license-gate store; re-renders if it's set/invalidated.
  const licenseGateSnapshot = useSyncExternalStore(subscribeLicenseGate, getLicenseGateSnapshot);
  // Seed from the store so an already-resolved user isn't re-checked or shown the license spinner
  // when navigating (the component remounts on every route change).
  const cachedForUser =
    user?.id != null && licenseGateSnapshot?.userId === String(user.id) ? licenseGateSnapshot : null;
  // Terminal non-cached outcome: a transient profile error or API failure. Lets us stop the spinner
  // and apply a safe fallback without poisoning the shared cache (so the next mount retries).
  const [checkFailed, setCheckFailed] = useState(false);
  const [userProfileRoles, setUserProfileRoles] = useState<string[]>([]); // Roles from database

  // licenseChecked / hasLicenseData are DERIVED from the store snapshot (which re-renders via
  // useSyncExternalStore) plus the local failure flag — never duplicated into state. This keeps
  // render in sync regardless of which concurrent effect run resolves the gate.
  const licenseChecked = cachedForUser != null || checkFailed;
  const hasLicenseData: boolean | null = cachedForUser
    ? cachedForUser.hasLicenseData
    : checkFailed
      ? false
      : null;

  // providerType is a build-time constant (VITE_AUTH_PROVIDER_TYPE); hook order is fixed per build.
  const msalResult = AUTH_API_CONFIG.providerType === 'msal' ? useMsal() : null;
  const { instance: msalInstance, inProgress } = msalResult || { instance: null, inProgress: InteractionStatus.None };
  const _msalIsAuthenticated = AUTH_API_CONFIG.providerType === 'msal' ? useIsAuthenticated() : false;
  void _msalIsAuthenticated;

  // Deterministic portal auth state for UI gating: allow access only when portal has validated
  const isAuthenticatedForRouting = authState === 'portal_authenticated' && !!user;
  const isAuthenticated = isAuthenticatedForRouting;
  
  const [hasAttemptedSilentAuth, setHasAttemptedSilentAuth] = useState(false);
  const silentAuthAttemptRef = useRef<string | null>(null); // Track which path we've attempted silent auth for

  // Log render information in development mode
  useEffect(() => {
    if (import.meta.env.DEV) {
      lastRenderTime.current = Date.now();
    }
  });

  // Resolve the license gate ONCE per signed-in user (not per route).
  // The result is cached at module scope so navigation between menus/pages never re-runs this.
  // This hook MUST be called before any conditional returns to follow React hooks rules.
  useEffect(() => {
    // Ignore flag: if deps change or the component unmounts mid-fetch, skip component state
    // updates from this (now stale) run. The module cache is still populated below because the
    // fetched result is valid for its own user id and benefits the next mount.
    let ignore = false;

    const resolveLicenseGate = async () => {
      if (!requireAuth || !isAuthenticatedForRouting || !user) {
        return;
      }

      const userId = user.id;
      const userIdStr = String(userId);

      // Already resolved for this user (survives remounts) — don't re-check or re-fetch.
      // Concurrent network calls are deduped at the data layer (getCachedOrFetch); the `ignore`
      // cleanup flag below prevents stale runs from updating state.
      if (getLicenseGate(userIdStr)) {
        return;
      }

      // Fetch user profile FIRST to get roles from database (response includes clinicianProfile).
      let numericUserId: number | null = null;
      let fetchedRoles: string[] = [];
      let profile: Awaited<ReturnType<typeof UserProfileApi.get>> | Awaited<ReturnType<typeof getCachedOrFetch>> = null;

      const extractRoles = (p: typeof profile): string[] => {
        if (!p?.roles || !Array.isArray(p.roles)) return [];
        return p.roles
          .map((role: any) => {
            if (typeof role === 'string') return role;
            if (role?.roleName) return role.roleName;
            if (role?.name) return role.name;
            if (role?.role) return role.role;
            return String(role);
          })
          .filter(Boolean);
      };

      try {
        const parsed = Number(userId);
        if (!Number.isNaN(parsed)) {
          numericUserId = parsed;
          try {
            profile = await UserProfileApi.get(parsed);
            fetchedRoles = extractRoles(profile);
            if (!ignore && fetchedRoles.length > 0) setUserProfileRoles(fetchedRoles);
          } catch (error) {
            // Could not fetch user profile for roles
          }
        } else if (typeof userId === 'string' && isEntraObjectId(userId)) {
          try {
            profile = await getCachedOrFetch(userId);
            numericUserId = profile?.userId ?? null;
            fetchedRoles = extractRoles(profile);
            if (!ignore && fetchedRoles.length > 0) setUserProfileRoles(fetchedRoles);
          } catch (error) {
            if (error instanceof ProfileFetchSuppressedError) {
              // Profile service temporarily unavailable; apply safe fallback but DON'T cache,
              // so the next mount retries instead of permanently gating the user.
              if (!ignore) setCheckFailed(true);
              return;
            }
            numericUserId = null;
          }
        }

        // Determine provider status from fetched roles, falling back to JWT token roles.
        const currentRoles = fetchedRoles.length > 0 ? fetchedRoles : (user?.roles || []);
        const selectedRole = localStorage.getItem('selectedRole');
        const isProvider = Boolean(
          currentRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
          (selectedRole && typeof selectedRole === 'string' && selectedRole.toLowerCase() === 'provider')
        );

        if (!isProvider) {
          // Non-providers never need a license check. Render derives from the store snapshot.
          setLicenseGate({ userId: userIdStr, isProvider: false, hasLicenseData: true });
          return;
        }

        if (!numericUserId) {
          localStorage.setItem('provider_license_completed', 'false');
          setLicenseGate({ userId: userIdStr, isProvider: true, hasLicenseData: false });
          return;
        }

        // Check clinician profile for license data (new structured fields or legacy details).
        const clinicianProfile = await UserProfileApi.getClinicianProfile(numericUserId);
        const licenseExists = UserProfileApi.hasLicenseDataInClinicianProfile(clinicianProfile);
        localStorage.setItem('provider_license_completed', licenseExists ? 'true' : 'false');
        setLicenseGate({ userId: userIdStr, isProvider: true, hasLicenseData: licenseExists });
      } catch (error) {
        // API failure: apply safe fallback for this mount, but DON'T cache so we retry next mount.
        localStorage.setItem('provider_license_completed', 'false');
        if (!ignore) setCheckFailed(true);
      }
    };

    void resolveLicenseGate();

    return () => {
      ignore = true;
    };
  }, [requireAuth, isAuthenticatedForRouting, user, licenseGateSnapshot]);

  // Attempt MSAL silent authentication - relies on Azure AD SSO session cookies
  // Each portal independently attempts silent auth, no dependency on shared storage
  // MSAL will use Azure AD session cookies if available, regardless of local cache
  useEffect(() => {
    const attemptSilentAuth = async () => {
      // Only attempt if:
      // 1. Authentication is required
      // 2. User is not authenticated
      // 3. Using MSAL provider
      // 4. MSAL instance exists
      // 5. MSAL is idle (not in progress)
      // 6. We haven't already attempted silent auth for this path
      if (
        !requireAuth ||
        isAuthenticated ||
        AUTH_API_CONFIG.providerType !== 'msal' ||
        !msalInstance ||
        inProgress !== InteractionStatus.None ||
        silentAuthAttemptRef.current === location.pathname
      ) {
        return;
      }

      // Mark that we're attempting silent auth for this path
      silentAuthAttemptRef.current = location.pathname;
      setHasAttemptedSilentAuth(true);

      try {
        // Use MSAL auth provider to attempt silent token acquisition
        // This relies on Azure AD SSO session cookies, not shared storage
        // The provider handles account retrieval internally
        const provider = getAuthProvider();
        if (!(provider instanceof MsalAuthProvider)) {
          if (import.meta.env.DEV) {
            console.warn('[ProtectedRoute] Auth provider is not MSAL provider');
          }
          return;
        }

        if (import.meta.env.DEV) {
          console.log('[ProtectedRoute] Attempting silent authentication via Azure AD SSO...');
          console.log('[ProtectedRoute] Origin:', window.location.origin);
          console.log('[ProtectedRoute] Pathname:', location.pathname);
        }

        // Attempt silent token acquisition - MSAL will use Azure AD session cookies
        // If no account exists locally, MSAL will attempt to use session cookies
        // If session cookies exist, token will be acquired silently
        // If no session exists, this will fail with interaction_required (expected)
        const token = await provider.getAccessTokenSilently();

        if (token) {
          // Silent auth succeeded - immediately refresh AuthContext to update isAuthenticated
          // This ensures AuthContext picks up the token without waiting
          try {
            await refreshAuthState();
            if (import.meta.env.DEV) {
              console.log('[ProtectedRoute] Silent authentication successful via Azure AD SSO');
              console.log('[ProtectedRoute] AuthContext refreshed after silent auth success');
            }
          } catch (refreshError) {
            console.warn('[ProtectedRoute] Failed to refresh AuthContext after silent auth:', refreshError);
          }
        } else {
          // No token returned - silent auth failed (no active account and no session)
          if (import.meta.env.DEV) {
            console.log('[ProtectedRoute] Silent authentication returned no token - redirecting to Azure AD');
          }
        }
      } catch (error) {
        // Silent auth failed - will redirect to Azure AD login
        // Don't log errors for interaction_required as that's normal (user needs to sign in)
        if (error instanceof Error) {
          const errorMessage = error.message.toLowerCase();
          if (
            !errorMessage.includes('interaction_required') &&
            !errorMessage.includes('consent_required') &&
            !errorMessage.includes('login_required')
          ) {
            if (import.meta.env.DEV) {
              console.warn('[ProtectedRoute] Silent authentication failed:', error.message);
            }
          } else {
            if (import.meta.env.DEV) {
              console.log('[ProtectedRoute] Silent auth requires user interaction - redirecting to Azure AD');
            }
          }
        }
        // Reset the attempt ref so we can try again if path changes
        // But only after a delay to avoid rapid retries
        setTimeout(() => {
          if (silentAuthAttemptRef.current === location.pathname) {
            silentAuthAttemptRef.current = null;
            setHasAttemptedSilentAuth(false);
          }
        }, 1000);
      }
    };

    void attemptSilentAuth();
  }, [
    requireAuth,
    isAuthenticated,
    msalInstance,
    inProgress,
    location.pathname,
    refreshAuthState,
  ]);

  // Logging effect - only runs when pathname or auth state changes (not on every render)
  useEffect(() => {
    // Removed console logs
  }, [location.pathname, requireAuth, isAuthenticated]);

  // Reset redirect tracking when authenticated or on login page
  useEffect(() => {
    // Reset redirect tracking when we're on sign in page or authenticated
    if (location.pathname === '/login' || location.pathname === '/signin' || isAuthenticated) {
      hasRedirectedRef.current = null;
    }
    
    // Reset silent auth attempt when pathname changes
    if (silentAuthAttemptRef.current !== location.pathname) {
      silentAuthAttemptRef.current = null;
      setHasAttemptedSilentAuth(false);
    }
  }, [location.pathname, isAuthenticated]);


  if (isLoading) {
    return withRouteShell(
      routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.restoringSession),
    );
  }

  // Enterprise logout: do not redirect when logout-in-progress; Azure redirect handles navigation.
  if (isLoggingOut) {
    return withRouteShell(
      routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.signingOut),
    );
  }

  if (authState === 'portal_validating') {
    return withRouteShell(
      routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.validatingAccount),
    );
  }

  if (requireAuth && !isAuthenticatedForRouting && !isLoggingOut && location.pathname !== '/login') {
    if (AUTH_API_CONFIG.providerType === 'msal' && isBrowserMsalOAuthRedirect()) {
      return withRouteShell(
        routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.authenticating),
      );
    }
    if (AUTH_API_CONFIG.providerType === 'msal') {
      if (inProgress !== InteractionStatus.None) {
        return withRouteShell(
          routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.authenticating),
        );
      }
      if (!hasAttemptedSilentAuth) {
        return withRouteShell(
          routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.checkingAuthentication),
        );
      }
      const currentPath = location.pathname;
      if (hasRedirectedRef.current !== currentPath) {
        hasRedirectedRef.current = currentPath;
      }
      const pca = getMsalInstance() ?? msalInstance;
      const emptyLocalMsalAccountCache =
        pca !== null &&
        hasEmptyMsalAccountCache(pca) &&
        hasPriorPortalAuthentication();
      if (emptyLocalMsalAccountCache) {
        return (
          <AuthRedirectGuard
            to="/session-expired"
            state={{ [HSUS_SESSION_EXPIRY_LOCATION_STATE_KEY]: 'idle' }}
          />
        );
      }
      return <AuthRedirectGuard state={{ from: location }} />;
    }

    const currentPath = location.pathname;
    if (hasRedirectedRef.current !== currentPath) {
      hasRedirectedRef.current = currentPath;
    }
    return <AuthRedirectGuard state={{ from: location }} />;
  }

  // If user is authenticated but trying to access sign in pages
  // BUT: Don't redirect if this is an MSAL redirect callback (has auth parameters in URL)
  // The MsalRedirectHandler will handle the redirect after processing authentication
  // CRITICAL: Check login error and isLoggingIn FIRST before evaluating isAuthenticated
  // This prevents race conditions where useIsAuthenticated() might return true before cache clearing takes effect
  if (!requireAuth && !isLoggingIn) {
    if (location.pathname === '/login' || location.pathname === '/signin') {
      const hasLoginError = sessionStorage.getItem('login_error') !== null;
      const portalNotAuthenticated = authState !== 'portal_authenticated' || !user;

      if (hasLoginError || portalNotAuthenticated) {
        if (hasLoginError) {
          console.log('[ProtectedRoute] Login error detected in sessionStorage, not redirecting from', location.pathname);
        }
        if (portalNotAuthenticated) {
          console.log('[ProtectedRoute] Portal not authenticated (state or no user), not redirecting from', location.pathname);
        }
        return withRouteShell(
          <Suspense fallback={SUSPENSE_PAGE_FALLBACK}>{children}</Suspense>,
        );
      }
    }
    
    // Check if this is an MSAL redirect callback by looking for auth parameters
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const isMsalCallback = (
      urlParams.has('code') ||
      urlParams.has('id_token') ||
      urlParams.has('access_token') ||
      hashParams.has('id_token') ||
      hashParams.has('access_token') ||
      urlParams.has('error') ||
      hashParams.has('error')
    );
    
    // Don't redirect if this is an MSAL callback - let MsalRedirectHandler handle it
    if (isMsalCallback) {
      return withRouteShell(
        <Suspense fallback={SUSPENSE_PAGE_FALLBACK}>{children}</Suspense>,
      );
    }
    
    // NOW check isAuthenticated - only redirect if user is actually authenticated
    // This check happens AFTER login_error and AuthContext checks to prevent race conditions
    if (isAuthenticatedForRouting) {
      // Redirect authenticated users away from sign-in pages
      // Signin page → choose-role
      // Login page → dashboard (preserve existing behavior)
      // Choose-role page → allow (part of registration flow)
      // AccountSetup page → allow (user can navigate away after logout)
      if (location.pathname === '/signin') {
        return <Navigate to="/choose-role" replace />;
      } else if (location.pathname === '/login') {
        const from = location.state?.from?.pathname || '/dashboard';
        return <Navigate to={from} replace />;
      } else if (
        location.pathname === '/choose-role' ||
        location.pathname.startsWith('/registration') ||
        location.pathname === '/account-setup'
      ) {
        // Allow authenticated users on registration flow pages and AccountSetup
        // This allows navigation from AccountSetup even after logout
        return withRouteShell(
          <Suspense fallback={SUSPENSE_PAGE_FALLBACK}>{children}</Suspense>,
        );
      }
      // Other pages default to dashboard
      const from = location.state?.from?.pathname || '/dashboard';
      return <Navigate to={from} replace />;
    }
  }

  // Check if authenticated user has completed license information (if required)
  // Skip this check for specific pages that don't require license completion
  if (requireAuth && isAuthenticatedForRouting && user) {
    const exemptPaths = [
      '/complete-license',
      '/registration/provider-license', // Allow access to provider license page during registration
      '/choose-role',
      '/registration',
      '/login',
      '/signin',
      '/unauthorized',
      '/profile',
      '/profile/edit-credentials', // Allow access to edit credentials page
    ];
    
    const isExemptPath = exemptPaths.some((exemptPath) => 
      location.pathname === exemptPath || location.pathname.startsWith(exemptPath + '/')
    );
    
    // Block initial render until license check completes to avoid mount/unmount flicker
    // This prevents a form render followed by a license-gated re-render.
    if (!isExemptPath && !licenseChecked) {
      return withRouteShell(
        routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.checkingLicenseStatus),
      );
    }
    
    // Check if user is a provider. Trust the resolved gate first (authoritative, survives remounts
    // where userProfileRoles is empty); otherwise derive from roles/active role.
    const userRoles = userProfileRoles.length > 0 ? userProfileRoles : (user?.roles || []);
    const selectedRole = localStorage.getItem('selectedRole');
    const roleToCheck = activeRole || selectedRole || userRoles[0];
    // FIXED: Ensure roleToCheck is a string before calling toLowerCase()
    const roleToCheckString = roleToCheck != null ? String(roleToCheck) : null;
    // FIXED: Ensure isProvider is always a boolean (not null/undefined)
    const isProvider = cachedForUser
      ? cachedForUser.isProvider
      : Boolean(
          (roleToCheckString && roleToCheckString.toLowerCase() === 'provider') ||
          availableRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
          userRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider')
        );

      // For providers on non-exempt paths, block until the one-time license check completes.
      // This prevents protected pages from mounting before license state is known.
      if (isProvider && !isExemptPath) {
        if (!licenseChecked) {
          return withRouteShell(
            routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.checkingLicenseStatus),
          );
        }
      // License check has completed - verify license data exists
      
      // Use hasLicenseData state (from API check) instead of just localStorage
      if (hasLicenseData === false) {
        // License data doesn't exist - redirect to complete-license
      return <Navigate to="/complete-license" replace />;
    }
    
      // If check completed but we don't have a result yet, use localStorage as fallback
      if (hasLicenseData === null) {
        const licenseCompleted = localStorage.getItem('provider_license_completed');
        if (licenseCompleted === 'false') {
      return <Navigate to="/complete-license" replace />;
        }
        // If localStorage says 'true' but API check returned null, be safe and block
        return withRouteShell(
          routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.verifyingLicenseStatus),
        );
      }
    }
  }

  // Check role-based access if required
  // Use activeRole for permission checks - user must have active role matching required roles
  if (requiredRoles && requiredRoles.length > 0) {
    const userRoles = user?.roles || [];
    // Check if active role matches any required role, or if user has any required role (fallback)
    const hasRequiredRole = activeRole 
      ? requiredRoles.includes(activeRole)
      : requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // FINAL SAFETY CHECK: If we reach here and user is a provider on a non-exempt path,
  // we should have already blocked or redirected. If not, block as a safety measure.
  if (requireAuth && isAuthenticatedForRouting && user) {
    // Trust the resolved gate first; otherwise derive from roles/active role.
    const userRoles = userProfileRoles.length > 0 ? userProfileRoles : (user?.roles || []);
    const selectedRole = localStorage.getItem('selectedRole');
    const roleToCheck = activeRole || selectedRole || userRoles[0];
    // FIXED: Ensure roleToCheck is a string before calling toLowerCase()
    const roleToCheckString = roleToCheck != null ? String(roleToCheck) : null;
    // FIXED: Ensure isProvider is always a boolean (not null/undefined)
    const isProvider = cachedForUser
      ? cachedForUser.isProvider
      : Boolean(
          (roleToCheckString && roleToCheckString.toLowerCase() === 'provider') ||
          availableRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider') ||
          userRoles.some(role => typeof role === 'string' && role.toLowerCase() === 'provider')
        );

    const exemptPaths = [
      '/complete-license',
      '/registration/provider-license',
      '/choose-role',
      '/registration',
      '/login',
      '/signin',
      '/unauthorized',
      '/profile',
      '/profile/edit-credentials',
    ];

    const isExemptPath = exemptPaths.some((exemptPath) =>
      location.pathname === exemptPath || location.pathname.startsWith(exemptPath + '/')
    );

    if (isProvider && !isExemptPath && !licenseChecked) {
      return withRouteShell(
        routeProgressSpinner(PROTECTED_ROUTE_LOADING_COPY.verifyingAccess),
      );
    }
  }

  return withRouteShell(
    <Suspense fallback={SUSPENSE_PAGE_FALLBACK}>{children}</Suspense>,
  );
};

// Don't memoize - children changes on navigation and we need to re-render when route changes
// Memoization was preventing children from mounting when navigating between routes
export const ProtectedRoute = ProtectedRouteComponent;
